import Image from 'next/image';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { getImageBasePath } from 'utils/generics';
import Header from 'widgets/header';
import * as Styles from './styles';
import { buildReportRequest } from './reportRequest';
import Calendar from '@acko-tech/building-blocks.ui.calendar';
import { useViewport } from 'utils/responsive';
import {
    ICommissionSummaryRequest,
    ICommissionSummaryResponse,
    IFiltersOptions,
} from './types';
import {
    downloadReportById,
    generateReport,
    getCommissionSummary,
    getReportHistory,
} from './api';
import { getDD_MM_YYYY, getYYYY_MM_DD } from 'utils/date';
import PaginatedTable from './PaginatedTable';
import SnackBar from 'library/snackbar';
import { Input, Checkbox } from '@acko-tech/building-blocks.ui.input';
import ConfirmationPopup from './ConfirmationPopup';
import {
    DEFAULT_FILTERS,
    ERROR_MESSAGES,
    FILTER_KEYS,
    NAVIGATION_TABS,
    SNACKBAR_TYPES,
    SUCCESS_MESSAGES,
} from './config';
import ReportCard, { ReportCardProps } from './ReportCard';
import {
    SegmentEventNameTypes,
    SegmentEventPages,
    SegmentEventActions,
    SegmentEventActionTypes,
} from 'utils/segment/constants';
import sendSegmentEvent from 'utils/segment';
import { MPosContext } from 'utils/context';

interface IProps {
    showHeader?: boolean;
}

const CommissionListingV1: React.FC<IProps> = (props) => {
    const { showHeader } = props;

    const [dateRange, setDateRange] = useState({
        from: new Date(new Date().setMonth(new Date().getMonth() - 1)),
        end: new Date(),
    });
    const { isMobile } = useViewport();
    const [loader, setLoader] = useState<boolean>(true);
    const [filterClicked, setFilterClicked] = useState(false);
    const [reportTypeOpen, setReportTypeOpen] = useState(false);
    const reportTypeRef = useRef<HTMLDivElement>(null);
    const [reportType, setReportType] = useState<'TRANSACTION' | 'DAILY_LOGIN'>(
        'TRANSACTION'
    );
    const isDailyLogin = reportType === 'DAILY_LOGIN';
    const [selectedKey] = useState('group_by');
    const [selectedFilterKey, setSelectedFilterKey] = useState('DATE');
    const [filters, setFilters] = useState<IFiltersOptions[]>(DEFAULT_FILTERS);
    const [secondaryFilters, setSecondaryFilters] = useState<IFiltersOptions[]>(
        []
    );
    const [selectedSecondaryFilters, setSelectedSecondaryFilters] = useState<
        string[]
    >([]);
    const [page, setPage] = useState<number>(0);
    const [pageData, setPageData] = useState<ICommissionSummaryResponse | null>(
        null
    );
    const [activeTab, setActiveTab] = useState<string>(NAVIGATION_TABS.RESULTS);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [isApiTriggered, setIsApiTriggered] = useState(false);
    const [calendarError, setCalendarError] = useState('');
    const [buttonLoader, setButtonLoader] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [confirmationModalShow, setConfirmationModalShow] = useState(false);
    const [reportsHistory, setReportsHistory] = useState<any>([]);
    const [searchText, setSearchText] = useState('');
    const [secondaryFilteredList, setSecondaryFilteredList] = useState<any[]>(
        []
    );
    const { userDetails, permissions } = useContext(MPosContext);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            sendSegmentEvent(
                SegmentEventNameTypes.VIEW,
                {
                    page_type: SegmentEventPages.VIEW_PAGE,
                    view_name: SegmentEventPages.COMMISSION_EARNED_V1_PAGE,
                },
                {
                    userDetails,
                    isMobile,
                }
            );
        }
    }, [window]);

    const getSummary = async (page) => {
        if (isDailyLogin) return;
        setLoader(true);
        try {
            const request: ICommissionSummaryRequest = {
                from_date: getDD_MM_YYYY(dateRange.from),
                to_date: getDD_MM_YYYY(dateRange.end),
                page,
                [selectedKey]: selectedFilterKey,
                user_group_name: permissions.group,
            };
            const data = await getCommissionSummary(
                permissions.partner_id,
                request
            );

            if (data?.status) {
                const selectedFilter = data?.filters_v2?.find(
                    (filter) => filter?.key === selectedKey
                );
                const selectedFilterValue = selectedFilter?.values?.find(
                    (value) => value?.key === selectedFilterKey
                ) as any;

                setFilters(data?.filters_v2);
                if (selectedFilterValue?.values?.length > 0) {
                    setSecondaryFilters(selectedFilterValue?.values);
                } else {
                    setSecondaryFilters([]);
                }
                setPageData(data as ICommissionSummaryResponse);
            } else {
                setSnackbar({
                    type: SNACKBAR_TYPES.FAILURE,
                    message:
                        data?.message ||
                        ERROR_MESSAGES.FETCH_COMMISSION_REPORTS_FAILED,
                });
                sendSegmentEvent(
                    SegmentEventNameTypes.EXCEPTION,
                    {
                        message:
                            data?.message ||
                            ERROR_MESSAGES.FETCH_COMMISSION_REPORTS_FAILED,
                    },
                    { userDetails, isMobile }
                );
            }
        } catch (e: any) {
            setSnackbar({
                type: SNACKBAR_TYPES.FAILURE,
                message:
                    e?.response?.data?.message ||
                    ERROR_MESSAGES.FETCH_COMMISSION_REPORTS_FAILED,
            });
            sendSegmentEvent(
                SegmentEventNameTypes.EXCEPTION,
                {
                    message:
                        e?.response?.data?.message ||
                        ERROR_MESSAGES.FETCH_COMMISSION_REPORTS_FAILED,
                },
                { userDetails, isMobile }
            );
        } finally {
            setLoader(false);
        }
    };

    const getReports = async () => {
        try {
            setLoader(true);
            const request: ICommissionSummaryRequest = {
                from_date: getDD_MM_YYYY(dateRange.from),
                to_date: getDD_MM_YYYY(dateRange.end),
                [selectedKey]: selectedFilterKey,
                sub_dealers:
                    selectedFilterKey === FILTER_KEYS.SUB_DEALER
                        ? selectedSecondaryFilters
                        : [],
                states:
                    selectedFilterKey === FILTER_KEYS.COUNTRY_STATE
                        ? selectedSecondaryFilters
                        : [],
                user_group_name: permissions.group,
            };
            const data = await getReportHistory(request);

            if (data?.status) setReportsHistory(data?.jobs);
            else {
                setSnackbar({
                    type: SNACKBAR_TYPES.FAILURE,
                    message:
                        data?.message ||
                        ERROR_MESSAGES.FETCH_COMMISSION_REPORTS_HISTORY_FAILED,
                });
                sendSegmentEvent(
                    SegmentEventNameTypes.EXCEPTION,
                    {
                        message:
                            data?.message ||
                            ERROR_MESSAGES.FETCH_COMMISSION_REPORTS_HISTORY_FAILED,
                    },
                    { userDetails, isMobile }
                );
            }
        } catch (e: any) {
            setSnackbar({
                type: SNACKBAR_TYPES.FAILURE,
                message:
                    e?.response?.data?.message ||
                    ERROR_MESSAGES.FETCH_COMMISSION_REPORTS_HISTORY_FAILED,
            });
            sendSegmentEvent(
                SegmentEventNameTypes.EXCEPTION,
                {
                    message:
                        e?.response?.data?.message ||
                        ERROR_MESSAGES.FETCH_COMMISSION_REPORTS_HISTORY_FAILED,
                },
                { userDetails, isMobile }
            );
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        if (!isApiTriggered) {
            getSummary(0);
            setIsApiTriggered(true);
        }
    }, []);

    useEffect(() => {
        if (activeTab === NAVIGATION_TABS.HISTORY) {
            getReports();
        }
    }, [activeTab]);

    useEffect(() => {
        if (isApiTriggered) {
            setPage(0);
            getSummary(0);
        }
    }, [selectedFilterKey, reportType]);

    useEffect(() => {
        if (isApiTriggered) {
            if (dateRange.from.getTime() > dateRange.end.getTime()) {
                setCalendarError('Start Date cannot be greater than End date');
            } else {
                setCalendarError('');
                getSummary(0);
            }
        }
    }, [dateRange]);

    useEffect(() => {
        if (isApiTriggered) {
            getSummary(page);
        }
    }, [page]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setFilterClicked(false);
            }
            if (
                reportTypeRef.current &&
                !reportTypeRef.current.contains(event.target as Node)
            ) {
                setReportTypeOpen(false);
            }
        };
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setFilterClicked(false);
                setReportTypeOpen(false);
            }
        };

        if (filterClicked || reportTypeOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [filterClicked, reportTypeOpen]);

    useEffect(() => {
        if (searchText?.length > 0) {
            const filteredList = secondaryFilters?.filter((filter) => {
                const value = filter?.display_name?.toLowerCase() || '';
                const searchQuery = searchText?.toLowerCase();

                if (
                    value?.includes(searchQuery) ||
                    searchQuery?.includes(value)
                ) {
                    return true;
                }
                return false;
            });
            setSecondaryFilteredList(filteredList);
        } else {
            setSecondaryFilteredList(secondaryFilters);
        }
    }, [searchText, secondaryFilters]);

    const onChange = (e: Date, id: string) => {
        switch (id) {
            case 'from':
                setDateRange((state) => ({
                    ...state,
                    from: e,
                }));
                break;
            case 'end':
                setDateRange((state) => ({
                    ...state,
                    end: e,
                }));
                break;
            default:
        }
    };

    const onNextPage = () => {
        setPage(page + 1);
    };

    const onPrevioudPage = () => {
        setPage(page - 1);
    };

    const handleGenerateReport = async () => {
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_type: SegmentEventActionTypes.TAP_BTN,
                action_name: SegmentEventActions.TAP_GENERATE_BTN,
                view_name: SegmentEventPages.COMMISSION_EARNED_V1_PAGE,
            },
            { userDetails, isMobile }
        );
        setButtonLoader(true);
        // Check for 180 days limit when generating report
        const daysDifference = Math?.ceil(
            (dateRange?.end?.getTime() - dateRange?.from?.getTime()) /
                (1000 * 60 * 60 * 24)
        );
        if (daysDifference > 90) {
            setSnackbar({
                type: SNACKBAR_TYPES.FAILURE,
                message: ERROR_MESSAGES.DATE_RANGE_ERROR,
            });
            sendSegmentEvent(
                SegmentEventNameTypes.EXCEPTION,
                {
                    message: ERROR_MESSAGES.DATE_RANGE_ERROR,
                    action_type: SegmentEventActionTypes.TAP_BTN,
                    action_name: SegmentEventActions.TAP_GENERATE_BTN,
                },
                { userDetails, isMobile }
            );
            setTimeout(() => setButtonLoader(false), 2000);
            return;
        }

        try {
            const request = buildReportRequest({
                fromDate: getYYYY_MM_DD(dateRange.from),
                toDate: getYYYY_MM_DD(dateRange.end),
                reportType,
                partnerId: permissions.partner_id,
                userGroupName: permissions.group,
                groupBy: selectedFilterKey,
                secondaryFilters: selectedSecondaryFilters,
            });
            const data = await generateReport(request);
            if (data?.status) {
                setConfirmationModalShow(true);
                setTimeout(() => setConfirmationModalShow(false), 4000);
                if (activeTab === NAVIGATION_TABS.HISTORY) getReports();
            } else {
                setSnackbar({
                    type: SNACKBAR_TYPES.FAILURE,
                    message:
                        data?.message || ERROR_MESSAGES.REPORT_GENERATE_FAILED,
                });
                sendSegmentEvent(
                    SegmentEventNameTypes.EXCEPTION,
                    {
                        message:
                            data?.message ||
                            ERROR_MESSAGES.REPORT_GENERATE_FAILED,
                        action_type: SegmentEventActionTypes.TAP_BTN,
                        action_name: SegmentEventActions.TAP_GENERATE_BTN,
                    },
                    { userDetails, isMobile }
                );
            }
        } catch (e: any) {
            setSnackbar({
                type: SNACKBAR_TYPES.FAILURE,
                message:
                    e?.response?.data?.message ||
                    ERROR_MESSAGES.REPORT_GENERATE_FAILED,
            });
            sendSegmentEvent(
                SegmentEventNameTypes.EXCEPTION,
                {
                    message:
                        e?.response?.data?.message ||
                        ERROR_MESSAGES.REPORT_GENERATE_FAILED,
                    action_type: SegmentEventActionTypes.TAP_BTN,
                    action_name: SegmentEventActions.TAP_GENERATE_BTN,
                },
                { userDetails, isMobile }
            );
            console.log('Error while generating report', e);
        } finally {
            setButtonLoader(false);
        }
    };

    const handleDownloadReport = async (requestId: string) => {
        setButtonLoader(true);
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_type: SegmentEventActionTypes.TAP_BTN,
                action_name: SegmentEventActions.TAP_DOWNLOAD_BTN,
                value: requestId,
                view_name: SegmentEventPages.COMMISSION_EARNED_V1_PAGE,
            },
            { userDetails, isMobile }
        );
        try {
            const data = await downloadReportById(requestId);

            if (
                data?.status &&
                data?.downloadUrl &&
                typeof window !== 'undefined'
            ) {
                setSnackbar({
                    type: SNACKBAR_TYPES.SUCCESS,
                    message: SUCCESS_MESSAGES.REPORT_DOWNLOADED,
                });
                window?.open(data?.downloadUrl, '_blank');
            } else {
                setSnackbar({
                    type: SNACKBAR_TYPES.FAILURE,
                    message:
                        data?.message || ERROR_MESSAGES.REPORT_DOWNLOAD_FAILED,
                });
                sendSegmentEvent(
                    SegmentEventNameTypes.EXCEPTION,
                    {
                        message:
                            data?.message ||
                            ERROR_MESSAGES.REPORT_DOWNLOAD_FAILED,
                        action_type: SegmentEventActionTypes.TAP_BTN,
                        action_name: SegmentEventActions.TAP_DOWNLOAD_BTN,
                    },
                    { userDetails, isMobile }
                );
            }
        } catch (e: any) {
            setSnackbar({
                type: SNACKBAR_TYPES.FAILURE,
                message:
                    e?.response?.data?.message ||
                    ERROR_MESSAGES.REPORT_DOWNLOAD_FAILED,
            });
            sendSegmentEvent(
                SegmentEventNameTypes.EXCEPTION,
                {
                    message:
                        e?.response?.data?.message ||
                        ERROR_MESSAGES.REPORT_DOWNLOAD_FAILED,
                    action_type: SegmentEventActionTypes.TAP_BTN,
                    action_name: SegmentEventActions.TAP_DOWNLOAD_BTN,
                },
                { userDetails, isMobile }
            );
            console.log('Error while downloading report', e);
        } finally {
            setButtonLoader(false);
        }
    };

    const getSelectedFilterDisplayName = () => {
        const selectedFilter = filters?.find(
            (filter) => filter.key === selectedKey
        );
        if (selectedFilter) {
            const selectedValueObj = selectedFilter?.values?.find(
                (value) => value.key === selectedFilterKey
            );
            return selectedValueObj
                ? selectedValueObj.display_name
                : selectedFilterKey;
        }
        return selectedFilterKey;
    };

    const getIconForFilter = (filterKey: string) => {
        const iconMap: { [key: string]: string } = {
            TRANSACTION: '/icons/transaction.svg',
            DATE: '/icons/calendar.svg',
            MONTH: '/icons/calendar.svg',
            SUB_DEALER: '/icons/building-icon.png',
            COUNTRY_STATE: '/icons/map-icon.png',
        };
        return iconMap[filterKey] || '/icons/transaction.svg';
    };

    return (
        <>
            {showHeader && (
                <>
                    <Header
                        wrapperStyles={{
                            maxWidth: isMobile ? '100%' : '80%',
                            margin: '0 auto',
                            padding: '20px',
                        }}
                    />
                    <Styles.Hr />
                </>
            )}
            <Styles.Wrapper>
                <Styles.Header>
                    <Styles.HeaderTextWrapper>
                        <Styles.HeaderText>
                            {'Download Report'}
                        </Styles.HeaderText>
                    </Styles.HeaderTextWrapper>
                </Styles.Header>
                <Styles.MainCard>
                    <Styles.DateRangeContainer>
                        <Styles.DateField>
                            <Styles.DateLabel>{'From'}</Styles.DateLabel>
                            <Calendar
                                title={''}
                                defaultDate={dateRange?.from}
                                onSelect={(date: Date) => {
                                    onChange(date, 'from');
                                    sendSegmentEvent(
                                        SegmentEventNameTypes.ACTION,
                                        {
                                            action_type:
                                                SegmentEventActionTypes.TAP_CALENDER,
                                            action_name:
                                                SegmentEventActions.TAP_DATE_TO_DATE_RANGE,
                                            date,
                                            view_name:
                                                SegmentEventPages.COMMISSION_EARNED_V1_PAGE,
                                        },
                                        { userDetails, isMobile }
                                    );
                                }}
                                enableWindow={{
                                    start: new Date(
                                        new Date().setFullYear(
                                            new Date().getFullYear() - 100
                                        )
                                    ),
                                    current: dateRange.from,
                                    end: new Date(),
                                }}
                                isDesktop={!isMobile}
                            />
                            {calendarError && calendarError !== null && (
                                <Styles.ErrorText>
                                    {calendarError}
                                </Styles.ErrorText>
                            )}
                        </Styles.DateField>
                        <Styles.DateField>
                            <Styles.DateLabel>{'To'}</Styles.DateLabel>
                            <Calendar
                                title={''}
                                defaultDate={dateRange.end}
                                onSelect={(date: Date) => {
                                    onChange(date, 'end');
                                    sendSegmentEvent(
                                        SegmentEventNameTypes.ACTION,
                                        {
                                            action_type:
                                                SegmentEventActionTypes.TAP_CALENDER,
                                            action_name:
                                                SegmentEventActions.TAP_DATE_FROM_DATE_RANGE,
                                            date,
                                            view_name:
                                                SegmentEventPages.COMMISSION_EARNED_V1_PAGE,
                                        },
                                        { userDetails, isMobile }
                                    );
                                }}
                                enableWindow={{
                                    start: new Date(
                                        new Date().setFullYear(
                                            new Date().getFullYear() - 100
                                        )
                                    ),
                                    current: dateRange.end,
                                    end: new Date(),
                                }}
                                isDesktop={!isMobile}
                            />
                        </Styles.DateField>
                    </Styles.DateRangeContainer>

                    <Styles.GroupByContainer>
                        <Styles.GroupByLabel as="label" htmlFor="report-type">
                            Report Type
                        </Styles.GroupByLabel>
                        <Styles.GroupByDropdownContainer ref={reportTypeRef}>
                            <Styles.GroupByInputWrapper
                                id="report-type"
                                role="button"
                                tabIndex={0}
                                aria-haspopup="listbox"
                                aria-expanded={reportTypeOpen}
                                aria-label="Report Type"
                                onClick={() => {
                                    setReportTypeOpen(!reportTypeOpen);
                                    setFilterClicked(false);
                                }}
                                onKeyDown={(event) => {
                                    if (
                                        event.key === 'Enter' ||
                                        event.key === ' '
                                    ) {
                                        event.preventDefault();
                                        setReportTypeOpen(!reportTypeOpen);
                                        setFilterClicked(false);
                                    }
                                }}
                            >
                                <Styles.GroupByIcon>
                                    <Image
                                        src={getImageBasePath(
                                            getIconForFilter(
                                                isDailyLogin
                                                    ? 'DATE'
                                                    : 'TRANSACTION'
                                            )
                                        )}
                                        alt=""
                                        width="16px"
                                        height="16px"
                                    />
                                </Styles.GroupByIcon>
                                <Styles.GroupByText>
                                    {isDailyLogin
                                        ? 'Daily Login Report'
                                        : 'Transaction'}
                                </Styles.GroupByText>
                                <Styles.DropdownIcon>
                                    <Image
                                        src={getImageBasePath(
                                            '/icons/chevron-down.svg'
                                        )}
                                        alt=""
                                        width="12px"
                                        height="12px"
                                    />
                                </Styles.DropdownIcon>
                            </Styles.GroupByInputWrapper>
                            {reportTypeOpen && (
                                <Styles.DropdownMenu
                                    role="listbox"
                                    aria-label="Report Type"
                                >
                                    {(
                                        ['TRANSACTION', 'DAILY_LOGIN'] as const
                                    ).map((type) => {
                                        const selectReportType = () => {
                                            setReportTypeOpen(false);
                                            if (type === reportType) {
                                                reportTypeRef.current
                                                    ?.querySelector<HTMLElement>(
                                                        '#report-type'
                                                    )
                                                    ?.focus();
                                                return;
                                            }
                                            setReportType(type);
                                            setSelectedSecondaryFilters([]);
                                            setSecondaryFilters([]);
                                            setFilterClicked(false);
                                            setSearchText('');
                                            setPage(0);
                                            reportTypeRef.current
                                                ?.querySelector<HTMLElement>(
                                                    '#report-type'
                                                )
                                                ?.focus();
                                        };
                                        return (
                                            <Styles.DropdownItem
                                                key={type}
                                                role="option"
                                                aria-selected={
                                                    reportType === type
                                                }
                                                tabIndex={0}
                                                isSelected={reportType === type}
                                                onClick={selectReportType}
                                                onKeyDown={(event) => {
                                                    if (
                                                        event.key === 'Enter' ||
                                                        event.key === ' '
                                                    ) {
                                                        event.preventDefault();
                                                        selectReportType();
                                                    }
                                                }}
                                            >
                                                <Styles.DropdownItemIcon>
                                                    <Image
                                                        src={getImageBasePath(
                                                            getIconForFilter(
                                                                type ===
                                                                    'DAILY_LOGIN'
                                                                    ? 'DATE'
                                                                    : 'TRANSACTION'
                                                            )
                                                        )}
                                                        alt=""
                                                        width="16px"
                                                        height="16px"
                                                    />
                                                </Styles.DropdownItemIcon>
                                                <Styles.DropdownItemText>
                                                    {type === 'DAILY_LOGIN'
                                                        ? 'Daily Login Report'
                                                        : 'Transaction'}
                                                </Styles.DropdownItemText>
                                                {reportType === type && (
                                                    <Styles.DropdownCheckIcon>
                                                        <Image
                                                            src={getImageBasePath(
                                                                '/icons/greenTick.svg'
                                                            )}
                                                            alt=""
                                                            width="16px"
                                                            height="16px"
                                                        />
                                                    </Styles.DropdownCheckIcon>
                                                )}
                                            </Styles.DropdownItem>
                                        );
                                    })}
                                </Styles.DropdownMenu>
                            )}
                        </Styles.GroupByDropdownContainer>
                    </Styles.GroupByContainer>
                    {!isDailyLogin && (
                        <Styles.GroupByContainer>
                            <Styles.GroupByLabel>
                                {'Group By'}
                            </Styles.GroupByLabel>
                            {isMobile ? (
                                <Styles.GroupByBoxesContainer>
                                    {filters
                                        ?.find(
                                            (filter) =>
                                                filter.key === selectedKey
                                        )
                                        ?.values?.map((value) => (
                                            <Styles.GroupByBox
                                                key={value.key}
                                                isSelected={
                                                    value.key ===
                                                    selectedFilterKey
                                                }
                                                onClick={() => {
                                                    setSelectedFilterKey(
                                                        value.key
                                                    );
                                                    sendSegmentEvent(
                                                        SegmentEventNameTypes.ACTION,
                                                        {
                                                            action_type:
                                                                SegmentEventActions.TAP_GROUPBY,
                                                            action_name:
                                                                value?.display_name,
                                                            view_name:
                                                                SegmentEventPages.COMMISSION_EARNED_V1_PAGE,
                                                        },
                                                        {
                                                            userDetails,
                                                            isMobile,
                                                        }
                                                    );
                                                }}
                                            >
                                                <Styles.BoxIcon>
                                                    <Image
                                                        src={getImageBasePath(
                                                            getIconForFilter(
                                                                value.key
                                                            )
                                                        )}
                                                        alt=""
                                                        width={'20px'}
                                                        height={'20px'}
                                                    />
                                                </Styles.BoxIcon>
                                                <Styles.BoxText
                                                    isSelected={
                                                        value.key ===
                                                        selectedFilterKey
                                                    }
                                                >
                                                    {value.display_name}
                                                </Styles.BoxText>
                                            </Styles.GroupByBox>
                                        ))}
                                </Styles.GroupByBoxesContainer>
                            ) : (
                                <Styles.GroupByDropdownContainer
                                    ref={dropdownRef}
                                >
                                    <Styles.GroupByInputWrapper
                                        onClick={() => setFilterClicked(true)}
                                    >
                                        <Styles.GroupByIcon>
                                            <Image
                                                src={getImageBasePath(
                                                    getIconForFilter(
                                                        selectedFilterKey
                                                    )
                                                )}
                                                alt=""
                                                width={'16px'}
                                                height={'16px'}
                                            />
                                        </Styles.GroupByIcon>
                                        <Styles.GroupByText>
                                            {getSelectedFilterDisplayName()}
                                        </Styles.GroupByText>
                                        <Styles.DropdownIcon>
                                            <Image
                                                src={getImageBasePath(
                                                    '/icons/chevron-down.svg'
                                                )}
                                                alt=""
                                                width={'12px'}
                                                height={'12px'}
                                            />
                                        </Styles.DropdownIcon>
                                    </Styles.GroupByInputWrapper>
                                    {filterClicked && (
                                        <Styles.DropdownMenu>
                                            {filters
                                                ?.find(
                                                    (filter) =>
                                                        filter.key ===
                                                        selectedKey
                                                )
                                                ?.values?.map((value) => (
                                                    <Styles.DropdownItem
                                                        key={value.key}
                                                        isSelected={
                                                            value.key ===
                                                            selectedFilterKey
                                                        }
                                                        onClick={() => {
                                                            setSelectedFilterKey(
                                                                value.key
                                                            );
                                                            setFilterClicked(
                                                                false
                                                            );
                                                            sendSegmentEvent(
                                                                SegmentEventNameTypes.ACTION,
                                                                {
                                                                    action_type:
                                                                        SegmentEventActions.TAP_GROUPBY,
                                                                    action_name:
                                                                        value?.display_name,
                                                                    view_name:
                                                                        SegmentEventPages.COMMISSION_EARNED_V1_PAGE,
                                                                },
                                                                {
                                                                    userDetails,
                                                                    isMobile,
                                                                }
                                                            );
                                                        }}
                                                    >
                                                        <Styles.DropdownItemIcon>
                                                            <Image
                                                                src={getImageBasePath(
                                                                    getIconForFilter(
                                                                        value.key
                                                                    )
                                                                )}
                                                                alt=""
                                                                width={'16px'}
                                                                height={'16px'}
                                                            />
                                                        </Styles.DropdownItemIcon>
                                                        <Styles.DropdownItemText>
                                                            {value.display_name}
                                                        </Styles.DropdownItemText>
                                                        {value.key ===
                                                            selectedFilterKey && (
                                                            <Styles.DropdownCheckIcon>
                                                                <Image
                                                                    src={getImageBasePath(
                                                                        '/icons/greenTick.svg'
                                                                    )}
                                                                    alt=""
                                                                    width={
                                                                        '16px'
                                                                    }
                                                                    height={
                                                                        '16px'
                                                                    }
                                                                />
                                                            </Styles.DropdownCheckIcon>
                                                        )}
                                                    </Styles.DropdownItem>
                                                ))}
                                        </Styles.DropdownMenu>
                                    )}
                                </Styles.GroupByDropdownContainer>
                            )}
                        </Styles.GroupByContainer>
                    )}
                    {!isDailyLogin && secondaryFilters?.length > 0 && (
                        <Styles.SecondaryFiltersWrapper>
                            <Styles.SecondaryFiltersHeaderText>
                                {`Select ${selectedFilterKey
                                    ?.toLowerCase()
                                    ?.replace('_', ' ')}`}
                            </Styles.SecondaryFiltersHeaderText>
                            <Styles.SearchWrapper>
                                <Input
                                    name={'search'}
                                    type={'text'}
                                    value={searchText}
                                    labelText={`Search`}
                                    onChange={(e) =>
                                        setSearchText(e?.target?.value)
                                    }
                                    rightElement={
                                        <>
                                            {searchText?.trim()?.length > 0 ? (
                                                <Image
                                                    src={getImageBasePath(
                                                        '/icons/close.svg'
                                                    )}
                                                    width={20}
                                                    height={20}
                                                    onClick={() =>
                                                        setSearchText('')
                                                    }
                                                    style={{
                                                        cursor: 'pointer',
                                                    }}
                                                />
                                            ) : (
                                                <Image
                                                    src={getImageBasePath(
                                                        '/images/search.svg'
                                                    )}
                                                    width={20}
                                                    height={20}
                                                />
                                            )}
                                        </>
                                    }
                                />
                            </Styles.SearchWrapper>
                            {secondaryFilteredList?.length > 0 ? (
                                <Styles.SecondaryFiltersContainer>
                                    {secondaryFilteredList?.map((filter) => (
                                        <Styles.SecondaryFilter
                                            key={filter?.key}
                                        >
                                            <Checkbox
                                                label={filter?.display_name}
                                                value={filter?.key}
                                                onChange={() => {
                                                    if (
                                                        selectedSecondaryFilters?.includes(
                                                            filter?.key
                                                        )
                                                    ) {
                                                        setSelectedSecondaryFilters(
                                                            (state) =>
                                                                state.filter(
                                                                    (item) =>
                                                                        item !==
                                                                        filter?.key
                                                                )
                                                        );
                                                    } else {
                                                        setSelectedSecondaryFilters(
                                                            (state) => [
                                                                ...state,
                                                                filter?.key,
                                                            ]
                                                        );
                                                    }
                                                    sendSegmentEvent(
                                                        SegmentEventNameTypes.ACTION,
                                                        {
                                                            action_type:
                                                                SegmentEventActionTypes.TAP_CHECKBOX,
                                                            action_name:
                                                                SegmentEventActions.TAP_SUB_DEALER_NAME,
                                                            value: filter?.display_name,
                                                            view_name:
                                                                SegmentEventPages.COMMISSION_EARNED_V1_PAGE,
                                                        },
                                                        {
                                                            userDetails,
                                                            isMobile,
                                                        }
                                                    );
                                                }}
                                                checked={selectedSecondaryFilters?.includes(
                                                    filter?.key
                                                )}
                                            />
                                            {filter?.display_name}
                                        </Styles.SecondaryFilter>
                                    ))}
                                </Styles.SecondaryFiltersContainer>
                            ) : (
                                <Styles.NoResult
                                    style={{
                                        justifyContent: 'left',
                                        color: 'rgb(216, 61, 55)',
                                    }}
                                >{`No results found for '${searchText}'`}</Styles.NoResult>
                            )}
                        </Styles.SecondaryFiltersWrapper>
                    )}
                    <Styles.GenerateButtonWrapper>
                        <Styles.GenerateButton
                            onClick={() => handleGenerateReport()}
                            disabled={buttonLoader}
                        >
                            <Styles.ButtonIcon>
                                <Image
                                    src={getImageBasePath(
                                        '/icons/download-icon.png'
                                    )}
                                    alt=""
                                    width={'20px'}
                                    height={'20px'}
                                />
                            </Styles.ButtonIcon>
                            {'Generate Report'}
                        </Styles.GenerateButton>
                    </Styles.GenerateButtonWrapper>
                </Styles.MainCard>

                <Styles.NavigationTabs>
                    <Styles.Tab
                        isActive={activeTab === NAVIGATION_TABS.RESULTS}
                        onClick={() => {
                            setActiveTab(NAVIGATION_TABS.RESULTS);
                            sendSegmentEvent(
                                SegmentEventNameTypes.ACTION,
                                {
                                    action_type:
                                        SegmentEventActionTypes.TAP_BTN,
                                    action_name:
                                        SegmentEventActions.TAP_REPORTS_TAB,
                                    view_name:
                                        SegmentEventPages.COMMISSION_EARNED_V1_PAGE,
                                },
                                { userDetails, isMobile }
                            );
                        }}
                    >
                        <Styles.TabIcon>
                            <Image
                                src={getImageBasePath('/icons/graph-icon.png')}
                                alt=""
                                width={'16px'}
                                height={'16px'}
                            />
                        </Styles.TabIcon>
                        {'Reports'}
                    </Styles.Tab>
                    <Styles.Tab
                        isActive={activeTab === NAVIGATION_TABS.HISTORY}
                        onClick={() => {
                            setActiveTab(NAVIGATION_TABS.HISTORY);
                            sendSegmentEvent(
                                SegmentEventNameTypes.ACTION,
                                {
                                    action_type:
                                        SegmentEventActionTypes.TAP_BTN,
                                    action_name:
                                        SegmentEventActions.TAP_REPORT_HISTORY_TAB,
                                    view_name:
                                        SegmentEventPages.COMMISSION_EARNED_V1_PAGE,
                                },
                                { userDetails, isMobile }
                            );
                        }}
                    >
                        <Styles.TabIcon>
                            <Image
                                src={getImageBasePath('/icons/clock-icon.svg')}
                                alt=""
                                width={'20px'}
                                height={'20px'}
                            />
                        </Styles.TabIcon>
                        {'Report History'}
                        {reportsHistory?.length > 0 && (
                            <Styles.Badge>
                                {reportsHistory?.length}
                            </Styles.Badge>
                        )}
                    </Styles.Tab>
                </Styles.NavigationTabs>

                {activeTab === NAVIGATION_TABS.RESULTS && isDailyLogin && (
                    <Styles.ListingWrapper>
                        <Styles.NoResult>
                            Generate the Daily Login Report, then download it
                            from Report History.
                        </Styles.NoResult>
                    </Styles.ListingWrapper>
                )}
                {activeTab === NAVIGATION_TABS.RESULTS &&
                    !isDailyLogin &&
                    (loader ? (
                        <Styles.LoaderBox
                            style={{
                                height: '100%',
                                display: 'grid',
                                placeItems: 'center',
                            }}
                        >
                            <Image
                                src={'/gif/pageLoader.gif'}
                                alt=""
                                height={isMobile ? '60px' : '100px'}
                                width={isMobile ? '60px' : '100px'}
                                style={{ margin: '0 auto' }}
                            />
                        </Styles.LoaderBox>
                    ) : pageData?.data && pageData?.data?.length > 0 ? (
                        <PaginatedTable
                            activeFilter={selectedFilterKey}
                            headers={pageData?.headers}
                            data={pageData?.data}
                            totalPages={pageData?.total_pages}
                            onNext={onNextPage}
                            onBack={onPrevioudPage}
                            currentPage={page + 1}
                            totalResults={pageData?.total_records}
                        />
                    ) : (
                        <Styles.ListingWrapper>
                            <Styles.NoResult>
                                {'No commissions'}
                            </Styles.NoResult>
                        </Styles.ListingWrapper>
                    ))}

                {activeTab === NAVIGATION_TABS.HISTORY &&
                    (loader ? (
                        <Styles.LoaderBox
                            style={{
                                height: '100%',
                                display: 'grid',
                                placeItems: 'center',
                            }}
                        >
                            <Image
                                src={'/gif/pageLoader.gif'}
                                alt=""
                                height={isMobile ? '60px' : '100px'}
                                width={isMobile ? '60px' : '100px'}
                                style={{ margin: '0 auto' }}
                            />
                        </Styles.LoaderBox>
                    ) : reportsHistory?.length > 0 ? (
                        <Styles.ListingWrapper>
                            <Styles.ReportHistoryContainer>
                                <Styles.ReportHistoryHeader>
                                    <Styles.ReportHistoryTitle>
                                        {'Report History'}
                                    </Styles.ReportHistoryTitle>
                                    <Styles.ReportHistoryCount>
                                        {`${reportsHistory?.length} reports`}
                                    </Styles.ReportHistoryCount>
                                </Styles.ReportHistoryHeader>
                                {reportsHistory?.map(
                                    (report: ReportCardProps) => (
                                        <ReportCard
                                            {...report}
                                            key={report?.request_id}
                                            handleDownloadReport={
                                                handleDownloadReport
                                            }
                                            isLoading={buttonLoader}
                                            sendSegmentEvent={sendSegmentEvent}
                                        />
                                    )
                                )}
                            </Styles.ReportHistoryContainer>
                        </Styles.ListingWrapper>
                    ) : (
                        <Styles.ListingWrapper>
                            <Styles.NoResult>{'No reports.'}</Styles.NoResult>
                        </Styles.ListingWrapper>
                    ))}
            </Styles.Wrapper>
            <ConfirmationPopup
                show={confirmationModalShow}
                handleSubmit={() => {
                    setConfirmationModalShow(false);
                    sendSegmentEvent(
                        SegmentEventNameTypes.ACTION,
                        {
                            page_type:
                                SegmentEventPages.GENERATE_REPORT_INFO_POPUP,
                            action_type: SegmentEventActionTypes.TAP_BTN,
                            action_name:
                                SegmentEventActions.TAP_GENERATE_REPORT_INFO_BTN_OK,
                            view_name:
                                SegmentEventPages.COMMISSION_EARNED_V1_PAGE,
                        },
                        { userDetails, isMobile }
                    );
                }}
                sendSegmentEvent={sendSegmentEvent}
                userDetails={userDetails}
            />
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() =>
                    setSnackbar({ type: '', message: '' })
                }
            />
        </>
    );
};

export default CommissionListingV1;
